import { Loader2, Star, Sparkles } from 'lucide-react'
import type { T2ICard } from '../../types/director'

interface CardGridProps {
  cards: T2ICard[]
  selectedCardId: number | null
  onSelectCard: (id: number) => void
  onGeneratePreview: (card: T2ICard, event: React.MouseEvent) => void
  previewImages: Map<number, { url: string; loading: boolean }>
  batchPreviewMode: boolean
  batchGenerating: boolean
  isGeneratingT2I: boolean
  onBatchPreview: () => void
  onOpenPreviewModal: (url: string, prompt: string) => void
  variantTargetCardId: number | null
  setVariantTargetCardId: (id: number | null) => void
  variantModification: string
  setVariantModification: (v: string) => void
  isGeneratingVariant: boolean
  onGenerateVariant: () => void
}

export function CardGrid({
  cards, selectedCardId, onSelectCard, onGeneratePreview,
  previewImages, batchPreviewMode, batchGenerating, isGeneratingT2I,
  onBatchPreview, onOpenPreviewModal,
  variantTargetCardId, setVariantTargetCardId,
  variantModification, setVariantModification,
  isGeneratingVariant, onGenerateVariant,
}: CardGridProps) {
  return (
    <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-gray-700">
      <div className="flex items-center mb-2">
        <h2 className="text-base font-semibold">🎨 第二步：选择你喜欢的画面方案</h2>
        {!isGeneratingT2I && (
          <button
            onClick={onBatchPreview}
            disabled={batchGenerating}
            className="ml-2 px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors text-xs font-medium flex items-center"
          >
            {batchGenerating ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" />批量生成中...</>
            ) : batchPreviewMode ? (
              '取消预览'
            ) : (
              '批量预览'
            )}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {cards.map((card) => {
          const previewState = previewImages.get(card.id)
          const hasPreview = previewState && previewState.url
          const isLoadingPreview = previewState && previewState.loading

          return (
            <div
              key={card.id}
              onClick={() => onSelectCard(card.id)}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all relative ${
                selectedCardId === card.id
                  ? 'border-blue-500 bg-blue-900/30'
                  : 'border-gray-600 bg-gray-700 hover:border-gray-500'
              }`}
            >
              {/* Star button */}
              <button
                onClick={(e) => onGeneratePreview(card, e)}
                disabled={isLoadingPreview}
                className="absolute top-1 right-1 p-1 bg-gray-800/80 hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10"
                title={hasPreview ? '查看预览图' : '生成预览图'}
              >
                {isLoadingPreview ? (
                  <Loader2 className="w-3 h-3 text-yellow-400 animate-spin" />
                ) : hasPreview ? (
                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                ) : (
                  <Star className="w-3 h-3 text-gray-400" />
                )}
              </button>

              {/* Variant button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setVariantTargetCardId(variantTargetCardId === card.id ? null : card.id)
                }}
                disabled={isGeneratingVariant}
                className="absolute top-1 right-7 p-1 bg-gray-800/80 hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10"
                title="生成变体"
              >
                <Sparkles className={`w-3 h-3 ${variantTargetCardId === card.id ? 'text-orange-400' : 'text-gray-400'}`} />
              </button>

              {/* Batch preview mode with image */}
              {batchPreviewMode && hasPreview ? (
                <div className="w-full">
                  <img
                    src={previewState.url}
                    alt={card.theme_name}
                    className="w-full h-auto rounded object-cover mb-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenPreviewModal(previewState.url, card.prompt)
                    }}
                  />
                  <h3 className="font-semibold text-xs">{card.theme_name}</h3>
                </div>
              ) : batchPreviewMode && isLoadingPreview ? (
                <div className="w-full h-32 flex items-center justify-center bg-gray-600 rounded mb-1">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : (
                <>
                  <div className="mb-1">
                    <span className="inline-block px-1.5 py-0.5 text-[10px] bg-purple-600 rounded">
                      {card.style_tag}
                    </span>
                  </div>
                  <h3 className="font-semibold mb-1 text-xs">{card.theme_name}</h3>
                  <p className="text-[10px] text-gray-400 mb-1">{card.aspect_ratio}</p>
                  <p className="text-[10px] text-gray-300 line-clamp-2">{card.prompt}</p>
                </>
              )}
            </div>
          )
        })}
      </div>

      {/* Variant generation panel */}
      {variantTargetCardId !== null && (
        <div className="mt-3 p-3 bg-gray-700 rounded-lg border border-orange-600/50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold flex items-center">
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-orange-400" />
              变体生成 — {cards.find((c) => c.id === variantTargetCardId)?.theme_name}
            </h3>
            <button
              onClick={() => setVariantTargetCardId(null)}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              取消
            </button>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={variantModification}
              onChange={(e) => setVariantModification(e.target.value)}
              placeholder="描述你想调整的方向，例如：换成夜景 / 色调改暖 / 加一个人物"
              className="flex-1 px-2 py-1.5 bg-gray-600 border border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white text-sm"
              disabled={isGeneratingVariant}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && variantModification.trim()) {
                  onGenerateVariant()
                }
              }}
            />
            <button
              onClick={onGenerateVariant}
              disabled={isGeneratingVariant || !variantModification.trim()}
              className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors text-sm whitespace-nowrap flex items-center"
            >
              {isGeneratingVariant ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />生成中...</>
              ) : (
                '生成 3 个变体'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
